import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ShieldCheck, Landmark, Coins, Wallet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, ProviderNotice } from "../components/common";

export default function Bridge() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/bridge/status"), api.get("/bridge/account")]);
        setStatus(s.data); setAccount(a.data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !status) return <FullLoader />;

  const ready = !status.setup_required;
  const kycOk = status.kyc_verified;
  const p = status.pricing;

  const guard = () => {
    if (!ready) { toast.error("Bridge is not connected yet (setup required after KYB)."); return false; }
    if (!kycOk) { toast.error("Verify your identity (KYC) first."); nav("/app/kyc"); return false; }
    return true;
  };

  const RampCard = ({ icon: Icon, title, desc, fee, testid }) => (
    <button data-testid={testid} onClick={() => guard() && toast.message("Bridge flow will open once your API keys are connected.")}
      className={`text-left p-5 rounded-2xl bg-card border border-border transition-transform active:scale-[0.99] ${ready ? "" : "opacity-70"}`}>
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary mb-3"><Icon size={20} /></span>
      <div className="font-display font-semibold">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      <div className="mt-3 inline-block text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{fee}</div>
    </button>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Bridge" subtitle={ready ? "Fiat ↔ stablecoin on & off-ramp" : "Stablecoin banking, when connected"} />

      <div className="rounded-3xl p-6 text-white mb-6 shadow-[0_18px_44px_rgba(75,35,204,0.3)]"
        style={{ background: "linear-gradient(135deg,#3A1A9F,#1B1340)" }}>
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-white/15"><Coins size={22} /></span>
          <div>
            <div className="font-display text-lg font-semibold">Stablecoin on/off-ramp</div>
            <div className="text-white/60 text-xs">Powered by Bridge · {status.environment}</div>
          </div>
          {ready && <span className="ml-auto text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-white/15">Live</span>}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {status.stablecoins.map((s) => (
            <span key={s} className="text-[11px] font-semibold bg-white/10 px-2.5 py-1 rounded-full">{s}</span>
          ))}
        </div>
      </div>

      {!ready && (
        <ProviderNotice name="Bridge" label="SETUP REQUIRED"
          message="Complete your KYB with Bridge, then add the Bridge API key. We never create fake blockchain transactions, deposits or balances — on/off-ramp activates automatically once your keys are connected." />
      )}

      {ready && !kycOk && (
        <button data-testid="bridge-kyc-cta" onClick={() => nav("/app/kyc")}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-warning/10 border border-warning/30 text-left mb-1">
          <ShieldCheck size={18} className="text-warning shrink-0" />
          <span className="text-sm font-medium text-warning">Verify your identity (KYC) to move money</span>
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        <RampCard icon={ArrowDownToLine} title="On-ramp" desc="Add USD → receive stablecoin via a virtual bank account"
          fee={`${p.onramp_pct}% fee`} testid="bridge-onramp" />
        <RampCard icon={ArrowUpFromLine} title="Off-ramp" desc="Convert stablecoin → USD with a liquidation address"
          fee={`${p.offramp_pct}% fee`} testid="bridge-offramp" />
      </div>

      <div className="mt-5 rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center gap-2 mb-3"><Landmark size={17} className="text-primary" /><h3 className="font-display font-semibold">Pricing</h3></div>
        <div className="grid grid-cols-2 gap-y-2 text-sm">
          <span className="text-muted-foreground">On-ramp</span><span className="text-right font-semibold">{p.onramp_pct}%</span>
          <span className="text-muted-foreground">Off-ramp</span><span className="text-right font-semibold">{p.offramp_pct}%</span>
          <span className="text-muted-foreground">USDT</span><span className="text-right font-semibold">{p.usdt_pct}%</span>
          <span className="text-muted-foreground">Virtual account / mo</span><span className="text-right font-semibold">${p.virtual_account_monthly}</span>
          <span className="text-muted-foreground">KYC / KYB</span><span className="text-right font-semibold">${p.kyc} / ${p.kyb}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">{p.note}</p>
      </div>

      {ready && account && (
        <div className="mt-5 rounded-2xl bg-card border border-border p-5">
          <div className="flex items-center gap-2 mb-3"><Wallet size={17} className="text-primary" /><h3 className="font-display font-semibold">Your Bridge account</h3></div>
          <div className="text-sm text-muted-foreground">KYC status: <b className="text-foreground">{account.kyc_status || "not started"}</b></div>
          <div className="text-sm text-muted-foreground">Virtual accounts: <b className="text-foreground">{account.virtual_accounts?.length || 0}</b> · Liquidation addresses: <b className="text-foreground">{account.liquidation_addresses?.length || 0}</b></div>
        </div>
      )}
    </div>
  );
}
